import React from "react";
import { Box, Typography, Button, IconButton, Avatar, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { useTranslation } from "react-i18next";
import { ValidationResult } from "ndr-designsystem";

const Root = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    borderRadius: 8,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
}));

const Header = styled(Box)(() => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
}));

const ItemRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
}));

const IndexAvatar = styled(Avatar)(({ theme }) => ({
    width: 32,
    height: 32,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 700,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
    color: theme.palette.text.secondary,
}));

interface RowEditProps {
    rowId: string;
    index: number;
    value: string;
    inputType: "text" | "number";
    validation: ValidationResult;
    canReorder: boolean;
    onValueChange: (value: string) => void;
    onRemove: () => void;
}

const RowEdit: React.FC<RowEditProps> = ({ rowId, index, value, inputType, validation, canReorder, onValueChange, onRemove }) => {
    const { t } = useTranslation("validation");
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: rowId,
        disabled: !canReorder,
    });

    return (
        <ItemRow
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            sx={{ opacity: isDragging ? 0.5 : 1, position: "relative", zIndex: isDragging ? 1 : "auto" }}
        >
            {canReorder ? (
                <DragIndicatorIcon
                    {...attributes}
                    {...listeners}
                    sx={{
                        width: 18,
                        height: 18,
                        color: "action.disabled",
                        cursor: "grab",
                        flexShrink: 0,
                        touchAction: "none",
                        "&:active": { cursor: "grabbing" },
                    }}
                />
            ) : (
                <Box sx={{ width: 18, flexShrink: 0 }} />
            )}
            <IndexAvatar>{index + 1}</IndexAvatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                    value={value}
                    type={inputType}
                    onChange={(e) => onValueChange(e.target.value)}
                    variant="standard"
                    fullWidth
                    size="small"
                    error={!validation.valid}
                    helperText={!validation.valid && validation.error ? t(validation.error) : undefined}
                />
            </Box>
            <IconButton
                size="small"
                onClick={onRemove}
                sx={{ color: "error.main", "&:hover": { bgcolor: "error.50", color: "error.dark" } }}
            >
                <DeleteOutlineIcon sx={{ width: 18, height: 18 }} />
            </IconButton>
        </ItemRow>
    );
};

interface ListInputProps {
    title: string;
    elements: (string | number)[] | undefined;
    onChange: (elements: string[]) => void;
    isEditing?: boolean;
    inputType?: "text" | "number";
    canReorder?: boolean;
    /** Per-item validation; the error is shown below the item's input. */
    checkValue?: (value: string) => ValidationResult;
    emptyText?: string;
    addButtonText?: string;
    maxItems?: number;
}

const ListInput: React.FC<ListInputProps> = ({
    title,
    elements,
    onChange,
    isEditing,
    inputType = "text",
    canReorder = false,
    checkValue,
    emptyText,
    addButtonText,
    maxItems,
}) => {
    const { t } = useTranslation("common");
    const items = (elements ?? []).map(e => `${e}`);
    const canAdd = maxItems == null || items.length < maxItems;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const rowIds = items.map((_, idx) => `item-${idx}`);

    const handleDragEnd = ({ active, over }: DragEndEvent): void => {
        if (!over || active.id === over.id) return;
        const oldIndex = rowIds.indexOf(String(active.id));
        const newIndex = rowIds.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
        const next = [...items];
        const [moved] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, moved);
        onChange(next);
    };

    const updateItem = (index: number, value: string): void => {
        const next = [...items];
        next[index] = value;
        onChange(next);
    };

    const removeItem = (index: number): void => {
        onChange(items.filter((_, idx) => idx !== index));
    };

    const counterText = maxItems != null ? ` (${items.length}/${maxItems})` : ` (${items.length})`;
    const resolvedEmptyText = emptyText ?? t("no_records_text");

    const renderEmpty = (): React.ReactElement => (
        <EmptyState>
            <ListAltIcon sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {resolvedEmptyText}
            </Typography>
        </EmptyState>
    );

    if (!isEditing) {
        return (
            <Root>
                <Typography variant="overline" sx={{ fontWeight: 600, color: "text.primary", letterSpacing: "0.08em" }}>
                    {title}{counterText}
                </Typography>
                {items.length === 0 ? renderEmpty() : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
                        {items.map((item, idx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <ItemRow key={`view-${idx}`}>
                                <IndexAvatar>{idx + 1}</IndexAvatar>
                                <Typography sx={{ flex: 1, fontWeight: 500, color: "text.primary", fontSize: "0.875rem" }}>
                                    {item}
                                </Typography>
                            </ItemRow>
                        ))}
                    </Box>
                )}
            </Root>
        );
    }

    return (
        <Root>
            <Header>
                <Typography variant="overline" sx={{ fontWeight: 600, color: "text.primary", letterSpacing: "0.08em" }}>
                    {title}{counterText}
                </Typography>
                <Button
                    size="small"
                    startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
                    disabled={!canAdd}
                    onClick={() => onChange([...items, ""])}
                    sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        ...(canAdd
                            ? {
                                bgcolor: "primary.50",
                                color: "primary.main",
                                border: 1,
                                borderColor: "primary.200",
                                "&:hover": { bgcolor: "primary.100" },
                            }
                            : {}),
                    }}
                >
                    {addButtonText ?? t("add_new")}
                </Button>
            </Header>

            {items.length === 0 ? renderEmpty() : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {items.map((item, idx) => (
                                <RowEdit
                                    key={rowIds[idx]}
                                    rowId={rowIds[idx]}
                                    index={idx}
                                    value={item}
                                    inputType={inputType}
                                    validation={item !== "" && checkValue ? checkValue(item) : { valid: true, error: null }}
                                    canReorder={canReorder}
                                    onValueChange={(value) => updateItem(idx, value)}
                                    onRemove={() => removeItem(idx)}
                                />
                            ))}
                        </Box>
                    </SortableContext>
                </DndContext>
            )}
        </Root>
    );
};

ListInput.defaultProps = {
    isEditing: false,
    inputType: "text",
    canReorder: false,
    checkValue: undefined,
    emptyText: undefined,
    addButtonText: undefined,
    maxItems: undefined,
};

export default ListInput;
